import React from "react"

import { Badge, Button, Skeleton } from "@coval/ui"
import { RefreshCw } from "lucide-react"

import { cn } from "@/utils/cn"

import { useDashboardDrilldownInsights } from "../hooks/useDashboardDrilldownInsights"
import type {
  DashboardDrilldownInsightRow,
  DashboardDrilldownInsightsInput,
  DashboardDrilldownInsightsStatus,
} from "../types"
import { DashboardInsightEvidenceText } from "./DashboardInsightEvidenceText"

interface DashboardDrilldownInsightsPanelProps {
  chart: DashboardDrilldownInsightsInput["chart"] | null
  rows: DashboardDrilldownInsightRow[]
  isRowsLoading: boolean
  rowsError: unknown
  onEvidenceLinkClick: (rowRef: string) => void
  onEvidenceLinkHover?: (rowRef: string | null) => void
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === "string") {
    return error
  }
  return "Unable to load the selected rows."
}

function LoadingInsights({
  statusSteps = [],
  streamedText = "",
}: {
  statusSteps?: DashboardDrilldownInsightsStatus[]
  streamedText?: string
}) {
  return (
    <div className="space-y-3" aria-label="Generating dashboard insights">
      {statusSteps.length > 0 ? (
        <div className="space-y-1.5 text-xs text-coval-text-muted">
          {statusSteps.map((step) => (
            <div key={step.stepId} className="flex items-center gap-2">
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full",
                  step.state === "loading" && "bg-amber-500",
                  step.state === "complete" && "bg-green-500",
                  step.state === "error" && "bg-red-500",
                )}
                aria-hidden
              />
              <span>{step.label}</span>
            </div>
          ))}
        </div>
      ) : null}
      {streamedText ? (
        <p className="whitespace-pre-wrap text-sm leading-5 text-coval-text-muted">
          {streamedText}
        </p>
      ) : null}
      <Skeleton className="h-4 w-3/4 rounded-md" />
      <Skeleton className="h-4 w-full rounded-md" />
      <Skeleton className="h-4 w-5/6 rounded-md" />
      <div className="flex gap-2 pt-1">
        <Skeleton className="h-6 w-16 rounded-sm" />
        <Skeleton className="h-6 w-20 rounded-sm" />
      </div>
    </div>
  )
}

function formatConfidence(confidence: "high" | "medium" | "low"): string {
  return `${confidence[0].toUpperCase()}${confidence.slice(1)} confidence`
}

export const DashboardDrilldownInsightsPanel: React.FC<
  DashboardDrilldownInsightsPanelProps
> = ({
  chart,
  isRowsLoading,
  onEvidenceLinkClick,
  onEvidenceLinkHover,
  rows,
  rowsError,
}) => {
  const canGenerate = !isRowsLoading && !rowsError && !!chart && rows.length > 0
  const {
    error,
    isLoading,
    isStale,
    output,
    regenerate,
    status,
    statusSteps,
    streamedText,
  } = useDashboardDrilldownInsights({
    chart,
    enabled: canGenerate,
    rows,
  })
  const showLoadingInsights = isLoading || isStale

  return (
    <section
      className="group flex h-full min-h-0 flex-col overflow-hidden rounded-sm border border-coval-border-active bg-surface-default"
      aria-label="Dashboard drilldown insights"
    >
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-coval-border px-4 py-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-coval-text/90">
            Insights
          </h3>
        </div>
        <Button
          type="button"
          variant="ghost"
          icon
          onClick={() => {
            regenerate()
          }}
          disabled={!canGenerate || isLoading}
          tooltip="Regenerate insights"
          className="opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
        >
          <RefreshCw
            aria-hidden
            className={cn("h-3.5 w-3.5", isLoading && "animate-spin")}
          />
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {rowsError ? (
          <div className="rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
            {getErrorMessage(rowsError)}
          </div>
        ) : isRowsLoading ? (
          <LoadingInsights />
        ) : rows.length === 0 ? (
          <p className="text-sm text-coval-text-muted">
            No rows were available for this chart point.
          </p>
        ) : showLoadingInsights ? (
          <LoadingInsights
            statusSteps={statusSteps}
            streamedText={streamedText}
          />
        ) : status === "error" && !output ? (
          <div className="space-y-3">
            <div className="rounded-sm border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
              {error ?? "Unable to generate insights."}
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                regenerate()
              }}
            >
              Try again
            </Button>
          </div>
        ) : output ? (
          <div className="space-y-3">
            <div aria-label="Executive summary" className="space-y-1">
              <p className="text-sm font-medium leading-5 text-coval-text">
                <DashboardInsightEvidenceText
                  text={output.headline}
                  evidenceLinks={output.insights.flatMap(
                    (insight) => insight.evidenceLinks,
                  )}
                  onEvidenceLinkClick={onEvidenceLinkClick}
                  onEvidenceLinkHover={onEvidenceLinkHover}
                />
              </p>
              <p className="text-sm leading-5 text-coval-text-muted">
                <DashboardInsightEvidenceText
                  text={output.summary}
                  evidenceLinks={output.insights.flatMap(
                    (insight) => insight.evidenceLinks,
                  )}
                  onEvidenceLinkClick={onEvidenceLinkClick}
                  onEvidenceLinkHover={onEvidenceLinkHover}
                />
              </p>
            </div>

            {output.insights.length > 0 ? (
              <ul className="space-y-2.5" aria-label="Insight bullets">
                {output.insights.map((insight, index) => (
                  <li
                    key={`${insight.title}-${index}`}
                    className="grid grid-cols-[0.4rem_1fr] gap-x-2.5 text-sm leading-5"
                  >
                    <span
                      className="mt-2 h-1.5 w-1.5 rounded-full bg-surface-2"
                      aria-hidden
                    />
                    <div className="min-w-0">
                      <p className="text-coval-text">
                        <Badge
                          variant="outline"
                          size="status"
                          className="mr-1.5 align-baseline"
                        >
                          {formatConfidence(insight.confidence)}
                        </Badge>
                        <span className="font-medium text-coval-text">
                          {insight.title}:{" "}
                        </span>
                        <DashboardInsightEvidenceText
                          text={insight.description}
                          evidenceLinks={insight.evidenceLinks}
                          onEvidenceLinkClick={onEvidenceLinkClick}
                          onEvidenceLinkHover={onEvidenceLinkHover}
                        />
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}

            {output.limitations && output.limitations.length > 0 ? (
              <div className="border-t border-coval-border pt-3">
                <ul className="space-y-1 text-xs leading-5 text-coval-text-muted">
                  {output.limitations.map((limitation) => (
                    <li key={limitation}>{limitation}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-coval-text-muted">Preparing insights...</p>
        )}
      </div>
    </section>
  )
}
